import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useAuth } from "context/AuthContext";
import { snackbarService } from "services/snackbarService";
import CustomText from "components/common/CustomText/CustomText";
import { colors } from "styles/colors";
import EditIcon from "icons/EditIcon";
import CustomDialog from "components/dialogs/CustomDialog";
import CustomButton from "components/buttons/CustomButton/CustomButton";
import { useParams } from "react-router-dom";
import { IPhotoSize } from "types/types";
import { useDeleteProductImage } from "hooks/useProduct";


interface Props {
  index: number;
  item: IPhotoSize;
  handleRemove?: (index: number) => void;
  isEditMode: boolean;
  onPreview: () => void;
}


export default function ProductImage({
  item,
  index,
  handleRemove,
  isEditMode = true,
  onPreview,
}: Props) {
  const { user } = useAuth();
  const productId = String(useParams().productId)

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteProductImage(
    () => {
      handleRemove?.(index);
      setDeleteOpen(false);
      snackbarService.showSnackbar("Image has been deleted successfully", "success");
    },
    (err: any) => snackbarService.showSnackbar(err, "error")
  );

  const handleDeleteImage = useCallback(() => {
    deleteMutation({
      productId,
      imageId: String(item.id),
    });
  }, [deleteMutation, item]);


  const handleRemoveImage = useCallback(() => {
    handleRemove?.(index);
  }, [handleRemove, index]);


  return (
    <>
      {/* Image Container */}
      <Box
        onClick={onPreview}
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "grey.200",
        }}
      >
        {/* Image */}
        <Grid key={item.id}>
          <Box
            sx={{
              position: 'relative',
              width: 'fit-content',
              borderRadius: 2,
            }}
            onClick={() => onPreview()}
          >

            <Avatar
              src={item.medium || ''}
              variant="rounded"
              sx={{
                width: 100,
                height: 100,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Grid>

        {/* Right-side action (options or remove) */}
        {isEditMode && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              display: "flex",
              alignItems: "center",
            }}
          >
            {handleRemove && <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setDeleteOpen(true);
              }}
              aria-label="remove"
              disableRipple
              sx={{
                padding: 0,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
              }}
            >
              <RemoveCircleOutlineIcon
                sx={{
                  color: colors.error,
                  fontSize: 20,
                  display: "block",
                }}
              />
            </IconButton>}
          </Box>
        )}

      </Box>

      {/* Remove / Delete Dialog */}
      <CustomDialog
        open={deleteOpen}
        title={item?.id ? "Delete Image" : "Remove Image"}
        message={item?.id
          ? "This image will be hidden from the product. It is kept on the server and can be restored."
          : "Are you sure you want to remove this image?"}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant='outlined'
            text='Cancel'
            textColor='error'
            onClick={() => setDeleteOpen(false)}
            height={35}
          />,
          <CustomButton
            key={'delete'}
            variant='filled'
            color='error'
            text={item?.id ? "Delete" : "Remove"}
            onClick={() => (item?.id ? handleDeleteImage() : handleRemoveImage())}
            height={35}
            loading={isDeleting}
          />,
        ]}
      />

    </>
  );
}
